import { TransactionShip } from '../objects/TransactionShip';
import { ShipFactory } from '../objects/ShipFactory';
import * as THREE from 'three';

export class ShipPool {
  private shipIdCounter: number = 0;
  private scene: THREE.Scene;
  protected active: Map<string, TransactionShip> = new Map();
  protected available: TransactionShip[] = [];
  protected maxSize: number;

  constructor(scene: THREE.Scene, initialSize: number = 50, maxSize: number = 1000) {
    this.scene = scene;
    this.maxSize = maxSize;
    ShipFactory.initialize();
    
    // Preload initial ships
    for (let i = 0; i < initialSize; i++) {
      const ship = this.createObject();
      this.resetObject(ship);
      this.available.push(ship);
    }
  }

  protected createObject(): TransactionShip {
    const shipId = `ship_${this.shipIdCounter++}`;
    const ship = ShipFactory.createShip(shipId);
    
    // Add to scene but keep invisible initially
    this.scene.add(ship.mesh);
    this.scene.add(ship.trail.mesh);
    ship.mesh.visible = false;
    ship.trail.mesh.visible = false;
    
    return ship;
  }

  protected resetObject(ship: TransactionShip): void {
    ship.deactivate();
    ship.trajectory.progress = 0;
    ship.trail.clear();
    ship.mesh.visible = false;
    ship.trail.mesh.visible = false;
    ship.mesh.position.set(0, 0, 0);
    ship.mesh.rotation.set(0, 0, 0);
    ship.mesh.scale.set(1, 1, 1);
  }

  public acquire(): TransactionShip {
    let ship: TransactionShip;
    
    if (this.available.length > 0) {
      ship = this.available.pop()!;
    } else if (this.active.size < this.maxSize) {
      ship = this.createObject();
    } else {
      throw new Error('Ship pool exhausted');
    }
    
    ship.mesh.visible = true;
    ship.trail.mesh.visible = true;
    this.active.set(ship.id, ship);
    return ship;
  }

  public release(ship: TransactionShip): void {
    this.active.delete(ship.id);
    this.resetObject(ship);
    this.available.push(ship);
  }

  public updateAll(deltaTime: number): void {
    // Update only active ships
    const activeShips = this.getActiveObjects();
    activeShips.forEach(ship => {
      if (ship.isActive) {
        ship.update(deltaTime);
      }
    });
  }

  public getActiveShips(): TransactionShip[] {
    return this.getActiveObjects().filter(ship => ship.isActive);
  }

  public getShipById(id: string): TransactionShip | undefined {
    return this.getActiveObjects().find(ship => ship.id === id);
  }

  public getActiveObjects(): TransactionShip[] {
    return Array.from(this.active.values());
  }

  public getAvailableObjects(): TransactionShip[] {
    return [];  // Not exposing available objects
  }

  public dispose(): void {
    // Remove all ships from scene before disposing
    const allShips = [...this.active.values(), ...this.available];
    allShips.forEach(ship => {
      if (ship.mesh.parent) {
        this.scene.remove(ship.mesh);
      }
      if (ship.trail.mesh.parent) {
        this.scene.remove(ship.trail.mesh);
      }
      ship.dispose();
    });
    
    this.active.clear();
    this.available.length = 0;
    ShipFactory.dispose();
  }

  public getStats() {
    return {
      active: this.active.size,
      available: this.available.length,
      total: this.active.size + this.available.length,
      maxSize: this.maxSize
    };
  }
}